import toast from 'react-hot-toast';

import { formatSplit, totalSamples } from '../../helpers/formatSplit';
import ButtonSubmit from '../ui/ButtonSubmit';
import { Pallets } from '../Pallets';
import { DefaultMap, InitialPallets, MainData, NewGrower } from '../../interfaces/intakes.reports';
import { DetailName, DirectField, Fruit, SectionName } from '../../interfaces/interfaces';
import { NewPallet, useAddNewPallet, useApplyToAll, useFinishReport, useSplitPallet } from '../../api/useReportTemp';
import { SpinnerCustom } from '../ui/SpinnerCustom';
import { palletData } from '../../data/pallet-steps';
import { ModalConfirmation } from '../ui/ModalConfirmation';
import { useEffect, useRef, useState } from 'react';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import { useNavigate } from 'react-router-dom';
import { isOneOrMore } from '../../helpers/numberActions';
import { PiEyedropperSample } from "react-icons/pi";
// import { hasUnripe } from '../../helpers/handlers';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { updateAllPalletsSection } from '../../helpers/updateSectionData';
import { EditorHTML } from '../customs/HTML-Text/EditorHTML';
// import { cleanHTML } from '../../helpers/stringHelpers';

const unripeComment = `\n\nA % of unripe fruit has been detected, nevertheless the outcome of the quality report, there could be a risk for rejection by the client.`

interface Props {
    mainData: MainData
    fruit: Fruit
    reportId: string
    pallets: InitialPallets[]
    setPallets: React.Dispatch<React.SetStateAction<InitialPallets[]>>
}

const CreateReportSteps = ({ mainData, fruit, reportId, setPallets, pallets }: Props) => {

    const samples = totalSamples(mainData.format) || 1
    const formatGr = formatSplit(mainData?.format) || 1
    const { mutate, isLoading, isError } = useAddNewPallet()
    const { mutateAsync } = useSplitPallet()
    const { mutate: mutateFinishReport, isLoading: isFinishing } = useFinishReport()
    const { mutate: mutateApplyToAll, isLoading: isApplying } = useApplyToAll()

    const isOnline = useOnlineStatus()

    const navigate = useNavigate()

    const [openConfirmation, setOpenConfirmation] = useState(false)
    const hasAddedComment = useRef(false)
    const [comments, setComments] = useState<string>(`<p>The results contained within this report were obtained from random samples taken throughout the delivery. Progressive defects that are present may exceed the eventual defect level specified. Sampling does not represents 100% of the real Quality and condition of the fruit and it is only an approximation to reality.</p><br><p>If, or on further processing, the product requires further selection to meet the customer specification, you will be informed of any losses in due course. However, if you would like the product returned or dealt with in any other manner please contact the relevant commercial contact within Growers Packers.</p>`)

    const [defaults, setDefaults] = useState<DefaultMap>(() => {
        const saved = localStorage.getItem(`defaults-${reportId}`);
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        localStorage.setItem(`defaults-${reportId}`, JSON.stringify(defaults));
    }, [defaults]);

    const { settings } = useTypedSelector(state => state.auth);

    const directFields: SectionName[] = ["score", "grade", "action"];

    const applyDefaultsToPallet = (pallet: InitialPallets) => {

        const updated: InitialPallets = { ...pallet };

        (Object.entries(defaults) as [SectionName, Record<string, any>][])
            .forEach(([section, fields]) => {
                if (directFields.includes(section)) {
                    if (fields[section] !== undefined) {
                        (updated as any)[section] = fields[section];
                    }
                    return;
                }

                const sectionData = (updated as any)[section];
                if (sectionData?.data) {
                    sectionData.data = sectionData.data.map((item: any) => {
                        if (fields?.[item.name] !== undefined) {
                            return { ...item, value: fields[item.name] };
                        }
                        return item;
                    });
                }
            });

        return updated;
    };

    const addNewPallet = (isSample?: boolean) => {

        //Si es un primer pallet o un sample, el número de pallet siempre será 1, sino se asigna el siguiente número disponible
        const palletNumber: number = (pallets.length === 0 || isSample) ? 1 : pallets[pallets.length - 1].pallet + 1
        const startPallet: InitialPallets = palletData(fruit, samples, formatGr, palletNumber, settings)

        const newPallet = applyDefaultsToPallet(startPallet)

        const data: NewPallet = {
            reportId,
            newPallet,
        }

        //Si es un sample agrega el número de sample, el pallet.pallet siempre es 1 ya que solo permite samples en 1 pallet
        if (isSample) {
            data.newPallet.pallet_sample = pallets.length + 1
            if (pallets.length === 1) {
                data.isStartingSample = pallets[0].pid
            }
        }

        mutate(data, {
            onSuccess: () => setPallets(pallets => {

                //Si se cambia a "modo sample", el Pallet cambia de nombre a Sample
                if( isSample && pallets.length === 1) {
                    pallets[0].pallet_sample = 1
                } 
                return [...pallets, newPallet]
            })
        })
    };

    const splitPallet = async (palletNumber: number, palletId: string, split: NewGrower, palletIndex: number) => {
        const newPallet: InitialPallets = palletData(fruit, samples, formatGr, palletNumber, settings)

        try {
            await mutateAsync({
                palletId,
                reportId,
                palletIndex,
                newPallet: {
                    ...newPallet,
                    splitPallet: split
                }
            })
        }
        catch (error) { console.log(error) }
    };

    //SEND Report
    const handleSend = async () => {

        if (!isOnline) return toast.error("You are offline, please check your connection")

        // mutateFinishReport({ reportId, comments: cleanHTML(comments) }, {
        mutateFinishReport({ reportId, comments: comments }, {
            onSuccess: async (data) => {
                navigate(`/report/${data.reportId}`)
            },
            onError: (error) => console.log(error)
        })
    }

    // If some pallet has unripe defect, add an extra comment
    const addDefectComment = (): void => {
        const hasUnripe = pallets.some(pallet =>
            isOneOrMore(pallet.pallgrow.major?.find(defect => defect.name === "unripe")?.value) ||
            isOneOrMore(pallet.pallgrow.minor?.find(defect => defect.name === "unripe")?.value)
        );

        if (hasUnripe && !hasAddedComment.current) {
            hasAddedComment.current = true;
            setComments(prev => `${prev}${unripeComment}`);
        }
    }

    const applyToAll = (
        section: SectionName,
        label: string,
        name: string,
        value: string | number | boolean | string[]
    ) => {

        mutateApplyToAll({ reportId, section, name, value, label }, {
            onSuccess: () => {
                setDefaults(prev => ({
                    ...prev,
                    [section]: {
                        ...((prev as any)[section] || {}),
                        [name]: value
                    }
                }));

                setPallets(prev =>
                    prev.map(pallet => {
                        if (directFields.includes(section as SectionName)) {
                            return { ...pallet, [section as keyof InitialPallets]: value as never };
                        }

                        return {
                            ...pallet,
                            [section]: updateAllPalletsSection(
                                (pallet as any)[section],
                                label, name, value
                            )
                        };
                    })
                );
            }
        })
    };

    if (isError) toast.error("Something went wrong, try again")

    return (
        <>
            {
                openConfirmation &&
                <ModalConfirmation
                    action={handleSend}
                    msg='Some pallets are incomplete. Would you like to finish the report anyway?'
                    closeConfirmation={() => setOpenConfirmation(false)}
                    buttonName='Finish Report'
                    isLoading={isFinishing}
                />
            }

            <div>
                {
                    pallets.length > 0
                        ?
                        pallets.map((pallet, index) => (
                            <Pallets
                                key={pallet.pid}
                                reportId={reportId}
                                index={index}
                                setPallets={setPallets}
                                pallets={pallets}
                                pallet={pallet}
                                isLastPallet={index === pallets.length - 1}
                                splitPallet={splitPallet}
                                addDefectComment={addDefectComment}
                                applyToAll={applyToAll}
                                defaults={defaults}
                                isLoading={isApplying}
                                addNewPallet={addNewPallet}
                                isAddingPallet={isLoading}
                            />
                        ))
                        : <p className="text-center bold mb-1">No pallets</p>
                }
            </div>

            {isLoading
                ? <SpinnerCustom center size='s' />
                : <div className='flex-center'>


                    <button
                        disabled={isFinishing || isApplying || isLoading}
                        onClick={() => addNewPallet(pallets.some(pallet => pallet.pallet_sample === 1))} className='btn-round'>
                        {
                            pallets.some(pallet => pallet.pallet_sample === 1)
                                ? <PiEyedropperSample size={20} className="tw-text-white" />
                                : "+"
                        }
                    </button>


                </div>
            }

            {/* <form className="pallet-comments mb-2 mt-3">
                <div className="mb-05">
                    <p className='mb-05 ml-05 bold'>General comments</p>
                    <textarea name="comments" id="comments" cols={30} rows={5} value={comments} onChange={(e) => setComments(e.target.value)} />
                </div>
            </form> */}

            <div className='tw-mt-5 tw-mb-8'>
                <p className='mb-05 ml-05 bold'>General comments</p>
                <EditorHTML value={comments} onChange={setComments} />
            </div>

            <ButtonSubmit
                isLoading={isFinishing}
                center
                title="Finish Report"
                action={() => {
                    if( pallets.length === 0) return toast.error("Add at least one pallet to finish the report")
                    if( pallets.some(pallet => pallet.complete === false || pallet.complete === null))
                        return setOpenConfirmation(true)
                    else handleSend()
                }}
            />
        </>
    )
}

export default CreateReportSteps